import {
  Table,
  TableHeading,
  TableCell,
  Button,
  Snackbar,
  Snack,
} from '@nike/eds';
import { Programs, NikeSwoosh } from '@nike/nike-design-system-icons';
import { Link, useLoaderData, useRevalidator } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import classnames from 'classnames';
import Loader from 'components/Loader';
import { QUERY_KEYS } from '../../constants';
import labellinClientAPI from '../../clientAPI/labelling';
import propagate403Error from '../../utils/propagate403Error';
import { useAtom } from 'jotai';
import {
  datasetsUpdateHasErrorAtom,
  datasetsUpdateMessageAtom,
} from '../../atoms/rewrite';
import { isExistScope } from '../../utils/authHelpers';

export default function Datasets() {
  const queryClient = useQueryClient();
  const loaderData = useLoaderData() as { access_token: string };
  const jwt = loaderData.access_token;
  const canWrite = isExistScope('search360:labeling:write', jwt);
  const revalidator = useRevalidator();
  const [updateMsg, setUpdateMsg] = useAtom(datasetsUpdateMessageAtom);
  const [updateHasError, setUpdateHasError] = useAtom(
    datasetsUpdateHasErrorAtom,
  );

  const {
    status,
    data: datasetList,
    error,
  } = useQuery({
    queryKey: [QUERY_KEYS.LABELLING_ALL_DATASET],
    queryFn: async () =>
      labellinClientAPI.findAllDataset(loaderData.access_token),
    staleTime: Infinity,
    onError: (err: { status: number }) =>
      err.status === 401 && revalidator.revalidate(),
  });

  const mutation = useMutation({
    mutationFn: (datasetId: string) => {
      return labellinClientAPI.publishDataset(
        datasetId,
        loaderData.access_token,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.LABELLING_ALL_DATASET],
      });
      setUpdateHasError(false);
      setUpdateMsg('Created!');
    },
    onError: (err: keyable) => {
      setUpdateHasError(true);
      setUpdateMsg(err.message || JSON.stringify(err));
      err.status === 401 && revalidator.revalidate();
    },
  });

  const publish = (datasetId: string) => {
    mutation.mutate(datasetId);
  };

  propagate403Error(error as keyable);
  if (status === 'loading') return <Loader />;
  if (!datasetList) {
    return (
      <div className='tw-w-full tw-h-2/3 tw-flex tw-items-center tw-justify-center'>
        <h2 className='eds-type--display-4'>No dataset available</h2>
      </div>
    );
  }
  return (
    <div className='tw-flex tw-justify-center'>
      <div className='tw-py-20 tw-w-2/3'>
        <p className='eds-type--display-5 tw-mb-4'>Dataset List</p>
        <Table className='tw-w-full'>
          <thead>
            <tr>
              <TableHeading>Name</TableHeading>
              <TableHeading>Status</TableHeading>
              <TableHeading>Analytics</TableHeading>
              <TableHeading>Action</TableHeading>
            </tr>
          </thead>
          <tbody>
            {datasetList.dataSets.map((dataset) => {
              const statusClass = classnames({
                '!tw-text-blue-500': dataset.status === 'INIT',
                '!tw-text-orange-500': dataset.status === 'PROCESSING',
                '!tw-text-green-500': dataset.status === 'PUBLISHED',
              });
              return (
                <tr key={dataset.dataSetKey}>
                  <TableCell>{dataset.description}</TableCell>
                  <TableCell className={statusClass}>
                    {dataset.status}
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/labelling/datasets/${dataset.dataSetKey}/report`}
                      className='tw-underline'
                    >
                      Report
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Button
                      size='small'
                      variant='secondary'
                      beforeSlot={<Programs />}
                      as={Link}
                      to={`/labelling/datasets/${dataset.dataSetKey}/products`}
                    >
                      Label
                    </Button>

                    {canWrite && (
                      <Button
                        size='small'
                        beforeSlot={<NikeSwoosh />}
                        onClick={publish.bind(null, dataset.dataSetKey)}
                      >
                        Publish
                      </Button>
                    )}
                  </TableCell>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      <Snackbar style={{ left: '90%', top: '90%' }}>
        {updateMsg && (
          <Snack
            autoDismissDuration={5000}
            id='typo-update-error-notification'
            onDismiss={(id) => {
              setUpdateMsg('');
            }}
            status={updateHasError ? 'error' : 'success'}
          >
            {updateMsg}
          </Snack>
        )}
      </Snackbar>
    </div>
  );
}
